import React, { useEffect, useMemo, useState } from 'react';
import cs from 'classnames';
import { Field, Form, Formik } from 'formik';

import TrendFileInput from 'features/trends/components/molecules/TrendFileInput';
import { useTrends } from 'features/trends/hooks/useTrends';
import { TRENDS_LIBRARY_COPY } from 'features/trends/pages/TrendsLibrary/copy';
import CenteredSpinner from 'features/ui/CenteredSpinner';
import TagsInput, { Value } from 'features/users/components/organisms/TagsInput';
import { Box, Button, Flex, Text } from 'shared/components/display';
import TextInput from 'shared/components/TextInput';

import { BUTTON_VARIANTS } from 'shared/styles/button';
import { Trend } from 'shared/typings/trends';

import { getValidationSchema, initialValues, TrendFormValues } from './config';
import styles from './styles';
import axiosClient from 'shared/axiosClient';
import { LinkCreator } from 'shared/utilities/linkUtility';
import { API_TREND_ROUTES } from 'shared/config/routes/api/apiTrendRoutes';

interface Props {
  values: Trend;
  onSubmit: (values: TrendFormValues) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  isNew?: boolean;
}
const TrendForm: React.FC<Props> = ({ values, onSubmit, onCancel, onDelete, isNew = false }) => {
  const [trendNames, setTrendNames] = useState<{
    names: Pick<Trend, 'id' | 'name'>[];
    loading: boolean;
  }>({
    names: [],
    loading: true,
  });

  const formattedTags: Value[] = Object.values(
    (values?.tags || []).reduce((acc, tag) => {
      const category = tag.category;
      if (!acc[category]) {
        acc[category] = {
          values: [tag.value],
          category,
        };
      } else {
        acc[category].values.push(tag.value);
      }
      return acc;
    }, []),
  );

  const defaultValues = useMemo(() => {
    return {
      ...initialValues,
      ...values,
      tags: formattedTags.length > 0 ? formattedTags : initialValues.tags,
    };
  }, [values, formattedTags]);

  useEffect(() => {
    fetchTrendNames();
    function fetchTrendNames() {
      axiosClient
        .httpRequest<Pick<Trend, 'id' | 'name'>[]>({
          url: LinkCreator.createApiLink({
            route: API_TREND_ROUTES.ALL_NAMES,
          }),
          method: 'GET',
        })
        .then(({ data }) => {
          setTrendNames({
            names: data,
            loading: false,
          });
        })
        .catch((error) => {
          setTrendNames((prev) => ({ ...prev, loading: false }));
        });
    }
  }, []);

  if (trendNames.loading) {
    return <CenteredSpinner />;
  }

  return (
    <Box css={styles} className={cs('trend-form')}>
      <Formik<TrendFormValues>
        initialValues={defaultValues}
        onSubmit={onSubmit}
        validationSchema={getValidationSchema(trendNames.names, values?.id)}
      >
        <Form>
          <Text className="trend-form__subheader">{TRENDS_LIBRARY_COPY.TREND_FORM_SUB_HEADER}</Text>
          <Field
            name="name"
            placeholder="Trend Name"
            label="Trend Name"
            component={TextInput}
            dataCy="trend-name-input"
          />
          <TagsInput
            name="tags"
            placeholder="Industry Tag"
            dataCy="industry-tag-input"
            allowAddCategory={false}
            theme="top-category-plain"
          />

          <Field name="file" component={TrendFileInput} />

          <Flex className="trend-form__button-wrapper">
            <Button type="button" className="trend-form__btn" onClick={onCancel} variant={BUTTON_VARIANTS.TOOL}>
              Cancel
            </Button>
            {!isNew && (
              <Button
                type="button"
                className={cs('trend-form__btn', 'trend-form__delete-btn')}
                onClick={onDelete}
                variant={BUTTON_VARIANTS.TOOL}
                dataCy="trend-form__delete-btn"
              >
                Delete
              </Button>
            )}
            <Button
              type="submit"
              className={cs('trend-form__btn', 'trend-form__submit-btn')}
              variant={BUTTON_VARIANTS.TOOL}
              dataCy="trend-form__submit-btn"
            >
              {isNew ? TRENDS_LIBRARY_COPY.BUTTON_CREATE_TREND : TRENDS_LIBRARY_COPY.BUTTON_EDIT_TREND}
            </Button>
          </Flex>
        </Form>
      </Formik>
    </Box>
  );
};

export default TrendForm;
